import { useMutation, useQuery } from "@apollo/client";
import { compact, uniqBy } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ComplianceProgramCalibrationGroupFragment,
  GetManagersOfUsersQuery,
  GetManagersOfUsersQueryVariables,
  SaveCalibrationGroupMutation,
  SaveCalibrationGroupMutationVariables,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import createOrUpdateCalibrationGroupMutation from "@apps/reporting/graphql/create-or-update-calibration-group-mutation";
import getComplianceProgramCalibrationGroupsQuery from "@apps/reporting/graphql/get-compliance-program-calibration-groups-query";
import getManagersOfUsersQuery from "@apps/reporting/graphql/get-managers-of-users-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, successNotificationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Input from "@components/input/input";
import Loading from "@components/loading/loading";
import RecipientDropdown from "@components/people-dropdown/recipient-dropdown";
import InfoTooltip from "@components/tooltip/info-tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import UserCombobox from "@components/user-combobox/user-combobox";
import { assertEdgesNonNull } from "@helpers/helpers";

import { useDeleteCalibrationGroup } from "./hooks";
import { CalibrationGroupFormType, UsersByCalibrationGroup } from "./types";

const emptyCalibrationGroup = (
  users: BasicUser[],
): CalibrationGroupFormType => ({
  name: "",
  users: users,
  managers: [],
  approvers: [],
});

const UserChip = ({
  user,
  suffix,
  removable,
  onRemove,
}: {
  user: BasicUser;
  suffix?: string;
  removable?: boolean;
  onRemove?: () => void;
}) => {
  return (
    <div className="bg-white border rounded-lg px-2 py-1 text-sm flex items-center gap-1">
      <Avatar user={user} size={4} />
      {user.name}
      {suffix}
      {removable && (
        <button
          className="ml-0.5 rounded-full p-0.5 border bg-gray-50 text-gray-600 hover:text-white hover:bg-blue-600 hover:border-blue-600"
          onClick={() => onRemove && onRemove()}
        >
          <TopicflowIcon type="close" className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

const Empty = () => <div className="text-gray-400">None</div>;

const CalibrationGroupForm = ({
  complianceProgramId,
  calibrationGroup,
  users,
  usersByCalibrationGroup,
  onSave,
  onClose,
  onCancel,
}: {
  complianceProgramId: number;
  calibrationGroup?: ComplianceProgramCalibrationGroupFragment | null;
  users: BasicUser[];
  usersByCalibrationGroup: UsersByCalibrationGroup;
  // Called when the group is saved, before onClose.
  onSave?: () => void;
  onClose: () => void;
  onCancel: () => void;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();

  const groupNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (groupNameInputRef.current) {
      const input = groupNameInputRef.current;
      const timeoutId = setTimeout(() => input.focus(), 200);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, []);

  const [proposedCalibrationGroup, setProposedComplianceProgram] =
    useState<CalibrationGroupFormType>(
      calibrationGroup
        ? {
            id: calibrationGroup.id,
            name: calibrationGroup.name,
            users: compact(calibrationGroup.users),
            managers: compact(calibrationGroup.managers),
            approvers: compact(calibrationGroup.approvers),
          }
        : emptyCalibrationGroup(users),
    );

  const usersAlreadyPartOfGroup = useMemo(() => {
    const allGroupUsers = Object.values(usersByCalibrationGroup).flatMap(
      (group) => group.users.map((user) => ({ user, name: group.name })),
    );
    const calibrationUserIds = proposedCalibrationGroup.users.map(
      ({ id }) => id,
    );
    return allGroupUsers.filter(({ user }) =>
      calibrationUserIds.includes(user.id),
    );
  }, [proposedCalibrationGroup, usersByCalibrationGroup]);

  const globalAdmins = useMemo(() => {
    const members = assertEdgesNonNull(organization.globalAdmins);
    return compact(members.map(({ user }) => user)).filter(
      ({ id }) => !users.find(({ id: userId }) => userId === id),
    );
  }, [organization.globalAdmins, users]);

  const excludedUserIds = useMemo(
    // people can only be part of one of users, managers, or approvers
    // so we can exclude them from the dropdown if they are already part of a group
    // (and also if they're a global admin, since they're implicitly included)
    () => [
      ...users.map(({ id }) => id),
      ...proposedCalibrationGroup.managers.map(({ id }) => id),
      ...proposedCalibrationGroup.approvers.map(({ id }) => id),
      ...globalAdmins.map(({ id }) => id),
    ],
    [globalAdmins, proposedCalibrationGroup, users],
  );

  // automatically load the managers of all users in the "to be calibrated" list,
  // filter out the global admins and managers may also be in the "to be calibrated" list
  // (a person can not be in the "mangers" of a calibration group including themselves)
  const { loading: isLoadingManagers } = useQuery<
    GetManagersOfUsersQuery,
    GetManagersOfUsersQueryVariables
  >(getManagersOfUsersQuery, {
    variables: {
      userIds: users.map(({ id }) => id),
    },
    skip: !!calibrationGroup?.id, // don't replace managers if we are editing an existing calibration group
    onCompleted: (response) => {
      const users = assertEdgesNonNull(response.users);
      setProposedComplianceProgram((prev) => ({
        ...prev,
        managers: uniqBy(
          compact(users.map(({ managersList }) => managersList).flat()),
          "id",
        ).filter(
          ({ id }) =>
            !globalAdmins.find(
              ({ id: globalAdminId }) => globalAdminId === id,
            ) && !users.find(({ id: userId }) => userId === id),
        ),
      }));
    },
  });

  const {
    trashCalibrationGroup,
    isDeleting,
    DeleteConfirmationDialog,
    confirmDelete,
  } = useDeleteCalibrationGroup();

  const [saveCalibrationGroup, { loading: isSaving }] = useMutation<
    SaveCalibrationGroupMutation,
    SaveCalibrationGroupMutationVariables
  >(createOrUpdateCalibrationGroupMutation);

  const handleDeleteGroup = useCallback(async () => {
    const confirmation = await confirmDelete();
    if (confirmation && proposedCalibrationGroup.id) {
      trashCalibrationGroup({
        variables: {
          id: proposedCalibrationGroup.id,
        },
        onCompleted: () => {
          successNotificationVar({
            title: "Calibration Group Deleted",
          });
          onCancel();
        },
      });
    }
  }, [
    confirmDelete,
    onCancel,
    proposedCalibrationGroup,
    trashCalibrationGroup,
  ]);

  const handleSaveGroup = useCallback(() => {
    saveCalibrationGroup({
      variables: {
        complianceProgramId,
        id: proposedCalibrationGroup.id,
        name: proposedCalibrationGroup.name,
        userIds: proposedCalibrationGroup.users.map(({ id }) => id),
        managerIds: proposedCalibrationGroup.managers.map(({ id }) => id),
        approverIds: proposedCalibrationGroup.approvers.map(({ id }) => id),
      },
      refetchQueries: [getComplianceProgramCalibrationGroupsQuery],
      onCompleted: () => {
        successNotificationVar({
          title: "Calibration Group Saved",
        });
        onSave?.();
        onClose();
      },
    });
  }, [
    complianceProgramId,
    proposedCalibrationGroup,
    saveCalibrationGroup,
    onSave,
    onClose,
  ]);

  const handleCancel = useCallback(() => onCancel(), [onCancel]);

  const formDisabled = isDeleting || isSaving;
  const saveBtnTooltip =
    usersAlreadyPartOfGroup.length > 0
      ? "An employee cannot be in more than one group."
      : !proposedCalibrationGroup.name.trim()
        ? "Please enter a name"
        : null;

  return (
    <div>
      <DeleteConfirmationDialog />

      <div className="flex items-center justify-between p-6">
        <div className="tf-header-xl">Calibration Group</div>
        <Button
          onClick={onClose}
          theme={buttonTheme.iconGray}
          icon={TopicflowIconMap.close}
        />
      </div>

      <div className="flex flex-col gap-6 max-h-[calc(100vh-10rem)] overflow-auto px-6 pb-6">
        <div className="flex flex-col gap-2">
          <div className="text-xs font-semibold text-gray-600">Group name</div>
          <Input
            ref={groupNameInputRef}
            disabled={formDisabled}
            value={proposedCalibrationGroup?.name}
            onChange={(evt) =>
              setProposedComplianceProgram((prev) => ({
                ...prev,
                name: evt.target.value,
              }))
            }
          />
        </div>

        <div className="border p-6 rounded-md flex flex-col gap-6">
          <div className="tf-header-base-dark">Employees to be Calibrated</div>
          <div className="flex flex-wrap gap-2">
            {proposedCalibrationGroup.users.map((user) => (
              <UserChip key={user.id} user={user} />
            ))}
          </div>
          {usersAlreadyPartOfGroup.length > 0 && (
            <div className="bg-amber-100 p-3 rounded-md flex gap-3">
              <TopicflowIcon
                type="warning"
                className="mt-1 h-4 w-4 text-amber-600"
              />
              <div>
                <div className="tf-header-sm-dark">
                  Some Employees Already in a Group
                </div>
                <div className="mt-1 text-gray-600">
                  Based on the filters applied, you have selected employees that
                  are already in a group. An employee cannot be in more than one
                  group.
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {usersAlreadyPartOfGroup.map((member) => (
                    <UserChip
                      key={member.user.id}
                      user={member.user}
                      suffix={`: ${member.name}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border p-6 rounded-md flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* eslint-disable-next-line local-rules/use-label */}
              <div className="tf-header-base-dark">Calibration Team</div>
              <InfoTooltip
                // eslint-disable-next-line local-rules/use-label
                text={`The calibration team will be able to view the submitted ${label("review")} data, and make comments and suggestions for calibration.`}
              />
            </div>
            <RecipientDropdown
              showCurrentUser
              excludeUserIds={excludedUserIds}
              recipients={proposedCalibrationGroup.managers}
              onChangeRecipient={(user) =>
                setProposedComplianceProgram((prev) => ({
                  ...prev,
                  managers: [...prev.managers, user],
                }))
              }
            >
              <UserCombobox.Button
                className="flex items-center"
                theme={buttonTheme.text}
              >
                <TopicflowIcon type="plus" className="h-4 w-4" />
                Add Person
              </UserCombobox.Button>
            </RecipientDropdown>
          </div>
          <div className="flex flex-wrap gap-2">
            {isLoadingManagers && <Loading mini />}
            {proposedCalibrationGroup.managers.map((user) => (
              <UserChip
                key={user.id}
                user={user}
                removable
                onRemove={() =>
                  setProposedComplianceProgram((prev) => ({
                    ...prev,
                    managers: prev.managers.filter(({ id }) => id !== user.id),
                  }))
                }
              />
            ))}
            {!isLoadingManagers &&
              proposedCalibrationGroup.managers.length === 0 && <Empty />}
          </div>
        </div>

        <div className="border p-6 rounded-md flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="tf-header-base-dark">Approvers</div>
              <InfoTooltip
                text={`Approvers will be able to view the submitted ${label("review")} data, and make comments, suggestions, and edit responses for calibration. They will also be able to update statuses and re-open ${label("review")} submissions for Managers.`}
              />
            </div>
            <RecipientDropdown
              showCurrentUser
              excludeUserIds={excludedUserIds}
              recipients={proposedCalibrationGroup.approvers}
              onChangeRecipient={(user) =>
                setProposedComplianceProgram((prev) => ({
                  ...prev,
                  approvers: [...prev.approvers, user],
                }))
              }
            >
              <UserCombobox.Button
                className="flex items-center"
                theme={buttonTheme.text}
              >
                <TopicflowIcon type="plus" className="h-4 w-4" />
                Add Person
              </UserCombobox.Button>
            </RecipientDropdown>
          </div>
          {globalAdmins.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="tf-header-sm-light">
                Automatically included (Global Admins)
              </div>
              <div className="flex flex-wrap gap-2">
                {globalAdmins.map((user) => (
                  <UserChip key={user.id} user={user} />
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <div className="tf-header-sm-light">Additional Inclusions</div>
            <div className="flex flex-wrap gap-2">
              {proposedCalibrationGroup.approvers.map((user) => (
                <UserChip
                  key={user.id}
                  user={user}
                  removable
                  onRemove={() =>
                    setProposedComplianceProgram((prev) => ({
                      ...prev,
                      approvers: prev.approvers.filter(
                        ({ id }) => id !== user.id,
                      ),
                    }))
                  }
                />
              ))}
              {proposedCalibrationGroup.approvers.length === 0 && <Empty />}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-6 pb-6">
        <div>
          {proposedCalibrationGroup.id && (
            <Button
              disabled={formDisabled}
              theme={buttonTheme.redDanger}
              text="Delete Group"
              onClick={handleDeleteGroup}
            />
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button
            disabled={formDisabled}
            theme={buttonTheme.default}
            text="Cancel"
            onClick={handleCancel}
          />
          <Button
            disabled={formDisabled || !!saveBtnTooltip}
            theme={buttonTheme.primary}
            tooltip={saveBtnTooltip}
            text="Save Group"
            onClick={handleSaveGroup}
          />
        </div>
      </div>
    </div>
  );
};

export default CalibrationGroupForm;
